import React from "react";
import { HolderOutlined } from "@ant-design/icons";
import { closestCenter, DndContext, DragOverlay, PointerSensor, KeyboardSensor, useDroppable, useSensor, useSensors, type Active, type DragEndEvent, type Over } from "@dnd-kit/core";
import { SortableContext, useSortable, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { Card, Checkbox, Grid, Space, Typography } from "../ui";
import { spacing } from "../settings/settingsListDesign";
import { dndTransformToCss } from "../settings/dndTransform";
import { dashboardCollision, dashboardDragPoint, dashboardDropSide, GAP_ID_PREFIX, isDashboardGapId, type DashboardDropSide } from "./dashboardDragCollision";
import { KEY_ENTER, KEY_SPACE } from "../ui/keyboardKeys";
import { groupByRowBreak, moveCardToNewRow, moveCardToTarget, resolveWidgetOrderWithBreaks } from "./dashboardOrder";
const designSpacing = { controlCompact: spacing.sm };
const designTypography = { fontSize: { xxs: 10, md: 16 } };
const designBorderRadius = { lg: 8 };
const DASHBOARD_ROWS_FILL_STYLE: React.CSSProperties = { display: "flex", flexDirection: "column", gap: spacing.lg, flex: 1, minHeight: 0 };
const DASHBOARD_STAT_ROW_STYLE: React.CSSProperties = { flex: "0 0 auto" };
// Listen behalten ihre Inhaltshöhe; nur zusätzliche freie Höhe wird verteilt.
const DASHBOARD_LIST_ROW_STYLE: React.CSSProperties = { flex: "1 0 auto", minHeight: 190 };
/** Haken „anzeigen" einer Karte im Layoutmodus; auch für Karten außerhalb des Rasters (z. B. eine Kopfkarte). */
export function DashboardCardCheckmark({ checked, onChange, label }: {
    checked: boolean;
    onChange: (v: boolean) => void;
    label: string;
}) {
    // Normales Ankreuzfeld: Füllung und Häkchen folgen dem Theme wie überall (ui/surface.css).
    return <Checkbox className="dashboard-card-checkmark" checked={checked} aria-label={label} onChange={(event) => onChange(event.target.checked)}/>;
}
const DROP_PLACEHOLDER_STYLE: React.CSSProperties = {
    minHeight: 80,
    width: "100%",
    borderRadius: designBorderRadius.lg,
    border: "2px dashed var(--ant-color-primary)",
    background: "var(--ant-color-primary-bg)",
    opacity: 0.6,
};
/** Stärke der Einfügemarke; sie sitzt mittig im Abstand zwischen Karten bzw. Zeilen. */
const DROP_MARK_SIZE = 4;
const DRAG_ACTIVATION_DISTANCE_PX = 8;
const DECIMAL_RADIX = 10;
const DROP_MARK_OFFSET = -(spacing.lg + DROP_MARK_SIZE) / 2;
const DROP_MARK_STYLE: React.CSSProperties = {
    position: "absolute",
    zIndex: 6,
    borderRadius: DROP_MARK_SIZE,
    background: "var(--ant-color-primary)",
    boxShadow: "0 0 0 3px var(--ant-color-primary-bg), 0 0 12px var(--ant-color-primary)",
    pointerEvents: "none",
};
/** Ziel beim Ziehen: eine Karte samt Einfügeseite oder ein Zwischenraum für eine neue Zeile. */
type DropTarget = { id: string; side: DashboardDropSide };
/**
 * Zwischenraum zwischen zwei Zeilen. Er nimmt keinen Platz ein und verschiebt beim Überfahren nichts:
 * Das Gitter bleibt während des Ziehens stehen, nur die Einfügemarke zeigt, wo die Karte landet.
 */
function RowGapDroppable({ gapId, marked }: {
    gapId: string;
    marked: boolean;
}) {
    const { setNodeRef } = useDroppable({ id: gapId });
    return (<div style={{ position: "relative", height: 0 }}>
      <div ref={setNodeRef} style={{ position: "absolute", left: 0, right: 0, top: -spacing.lg / 2 - 12, height: 24 }}/>
      {marked ? <div style={{ ...DROP_MARK_STYLE, left: 0, right: 0, top: DROP_MARK_OFFSET, height: DROP_MARK_SIZE }}/> : null}
    </div>);
}
/** Einfügemarke an einer Zielkarte; an den Außenkanten der Zeile liegt sie innen, damit sie nicht abgeschnitten wird. */
function CardDropMark({ side, stacked, outerEdge }: { side: DashboardDropSide; stacked: boolean; outerEdge: boolean }) {
    const offset = outerEdge ? 0 : DROP_MARK_OFFSET;
    const edge = stacked ? (side === "before" ? "top" : "bottom") : (side === "before" ? "left" : "right");
    const style: React.CSSProperties = stacked
        ? { ...DROP_MARK_STYLE, left: 0, right: 0, height: DROP_MARK_SIZE, [edge]: offset }
        : { ...DROP_MARK_STYLE, top: 0, bottom: 0, width: DROP_MARK_SIZE, [edge]: offset };
    return <div style={style}/>;
}
/** Höchstbreite der Ziehvorschau. */
const DRAG_PREVIEW_MAX_WIDTH = 360;
/** Innerhalb einer Zeile rücken keine Karten nach — das Ziel zeigt allein die Einfügemarke. */
const NO_SHIFT_STRATEGY = () => null;
function SortableDashboardCard({ id, checked, onToggle, children, cardTitle, cardExtra, periodSelector, dropMark, controls, }: {
    id: string;
    checked: boolean;
    onToggle: (v: boolean) => void;
    children: React.ReactNode;
    cardTitle?: React.ReactNode;
    cardExtra?: React.ReactNode;
    /** Zeitraum-Auswahl der Karte; nur gesetzt, wenn die Karte einen Zeitraum kennt. */
    periodSelector?: React.ReactNode;
    /** Einfügemarke, wenn diese Karte gerade Ziel ist. */
    dropMark?: React.ReactNode;
    controls: { show: string; drag: string };
}) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style: React.CSSProperties = {
        transform: dndTransformToCss(transform),
        transition,
        flex: 1,
        minWidth: 0,
        position: "relative",
    };
    // Die gezogene Karte bleibt unsichtbar stehen, der Platzhalter liegt darüber: So ändert sich beim
    // Ziehbeginn weder ein Maß noch das Wurzelelement, und die zu Beginn gemessenen Trefferrechtecke
    // bleiben bis zum Loslassen exakt.
    return (<div ref={setNodeRef} style={style}>
      {isDragging ? <div style={{ ...DROP_PLACEHOLDER_STYLE, position: "absolute", inset: 0, minHeight: 0 }}/> : dropMark}
      <Card className={checked ? "dashboard-config-card" : "dashboard-config-card dashboard-config-card--hidden"} style={{ borderStyle: "dashed", height: "100%", display: "flex", flexDirection: "column", visibility: isDragging ? "hidden" : undefined }} styles={{ body: { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" } }}>
        <div style={{ display: "flex", alignItems: "stretch", gap: spacing.md, minHeight: 60, flex: 1 }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: designSpacing.controlCompact, flexShrink: 0 }}>
            <DashboardCardCheckmark checked={checked} onChange={onToggle} label={controls.show}/>
            <div {...attributes} {...listeners} aria-label={controls.drag} style={{ cursor: "grab", display: "flex", flex: 1, alignItems: "center", justifyContent: "center", padding: spacing.xs }}>
              <HolderOutlined style={{ color: "var(--ant-color-text-tertiary)", fontSize: designTypography.fontSize.md }}/>
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
            {(cardTitle != null || cardExtra != null || periodSelector != null) && (<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: spacing.sm, flexWrap: "wrap", marginBottom: spacing.sm }}>
                <span className="mq-dashboard-card-title" style={{ minWidth: 0, overflowWrap: "anywhere" }}>{cardTitle}</span>
                <Space size={spacing.sm} wrap>
                  {periodSelector}
                  {cardExtra}
                </Space>
              </div>)}
            <div style={{ flex: 1 }}>{children}</div>
          </div>
        </div>
      </Card>
    </div>);
}
/**
 * Kam der Klick von einem eigenen Ziel innerhalb der Karte (Zeilen-Link, Knopf)? Dann gilt nur dieses —
 * sonst navigierten Zeile und Karte nacheinander.
 */
function fromInnerControl(event: React.SyntheticEvent): boolean {
    const control = (event.target as HTMLElement | null)?.closest?.("a, button, input, select, textarea, [role='link'], [role='button']");
    return control != null && control !== event.currentTarget;
}
export interface DashboardCardMeta {
    /** Erstladen dieser Karte; Hintergrundabrufe bestätigter Daten bleiben unabhängig. */
    loading?: boolean;
    cardTitle?: React.ReactNode;
    cardExtra?: React.ReactNode;
    /** Symbol vor dem Kartentitel; in derselben Kachelgestaltung wie die Kennzahlen (`StatCardContent icon`). */
    cardIcon?: React.ReactNode;
}
function withCardIcon({ cardTitle, cardExtra, cardIcon, loading }: DashboardCardMeta): DashboardCardMeta {
    if (cardIcon == null || cardTitle == null) return { cardTitle, cardExtra, loading };
    return { cardTitle: <><span className="mq-stat-card-icon mq-dashboard-card-icon" aria-hidden>{cardIcon}</span><span className="mq-dashboard-card-label">{cardTitle}</span></>, cardExtra, loading };
}
export function DashboardGrid({ allWidgetKeys, allowedWidgetKeys, widgetOrder, setWidgetOrder, configMode, isWidgetVisible, setWidgetVisible, getWidgetMeta, renderWidgetBody, renderPeriodSelector = () => null, periodLabel = () => null, widgetTarget = () => undefined, listWidgetKeys = new Set<string>(), controls, disabled = false }: {
    controls: { show: string; drag: string };
    disabled?: boolean;
    allWidgetKeys: string[];
    allowedWidgetKeys: ReadonlySet<string>;
    widgetOrder: string[];
    setWidgetOrder: React.Dispatch<React.SetStateAction<string[]>>;
    configMode: boolean;
    isWidgetVisible: (key: string) => boolean;
    setWidgetVisible: (key: string, visible: boolean) => void;
    getWidgetMeta: (key: string) => DashboardCardMeta;
    renderWidgetBody: (key: string) => React.ReactNode;
    renderPeriodSelector?: (key: string) => React.ReactNode;
    periodLabel?: (key: string) => string | null;
    widgetTarget?: (key: string) => (() => void) | undefined;
    listWidgetKeys?: ReadonlySet<string>;
}) {
    const screens = Grid.useBreakpoint();
    const isMobile = screens.md === false;
    const [activeDragId, setActiveDragId] = React.useState<string | null>(null);
    const [activeDragWidth, setActiveDragWidth] = React.useState<number | null>(null);
    const [dropTarget, setDropTarget] = React.useState<DropTarget | null>(null);
    /** Letzter Bezugspunkt der Trefferermittlung — derselbe, nach dem dnd-kit das Ziel bestimmt hat. */
    const dragPointRef = React.useRef({ x: 0, y: 0 });
    const dragStartOrderRef = React.useRef<string[] | null>(null);
    const orderedKeysWithBreaks = resolveWidgetOrderWithBreaks(widgetOrder, allWidgetKeys);
    const configModeRows = groupByRowBreak(orderedKeysWithBreaks, allWidgetKeys);
    const displayRows = configModeRows.map(row => row.filter(key => isWidgetVisible(key) && allowedWidgetKeys.has(key))).filter(row => row.length > 0);
    const configModeRowsFiltered = configModeRows.map((keys, sourceRowIndex) => ({ keys: keys.filter(key => allowedWidgetKeys.has(key)), sourceRowIndex })).filter(row => row.keys.length > 0);
    const gapTargetIndexes = [configModeRowsFiltered[0]?.sourceRowIndex ?? 0, ...configModeRowsFiltered.map((_, index) => configModeRowsFiltered[index + 1]?.sourceRowIndex ?? configModeRows.length)];
    const LIST_WIDGET_KEYS = listWidgetKeys;
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE_PX } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
    const collisionDetection = React.useCallback((args: Parameters<typeof closestCenter>[0]): ReturnType<typeof closestCenter> => {
        dragPointRef.current = dashboardDragPoint(args);
        return dashboardCollision(args);
    }, []);
    /** Reihenfolge nach dem Ablegen auf `target`. Zeilenindizes der Zwischenräume beziehen sich auf die ungefilterte Reihenfolge. */
    const applyDrop = (order: string[], activeId: string, target: DropTarget): string[] => isDashboardGapId(target.id)
        ? moveCardToNewRow(order, activeId, parseInt(target.id.slice(GAP_ID_PREFIX.length), DECIMAL_RADIX))
        : moveCardToTarget(order, activeId, target.id, target.side === "before");
    const sameRows = (a: string[], b: string[]) => JSON.stringify(groupByRowBreak(a, allWidgetKeys)) === JSON.stringify(groupByRowBreak(b, allWidgetKeys));
    /**
     * Ziel aus dem aktuellen Treffer. Kein Ziel — und damit keine Marke —, wo sich nichts ändern würde:
     * über der eigenen Karte, an ihren eigenen Kanten und bei einer Karte allein in ihrer Zeile im
     * Zwischenraum direkt darüber oder darunter.
     */
    const resolveDropTarget = (active: Active, over: Over | null): DropTarget | null => {
        if (!over) return null;
        const overId = String(over.id);
        const activeId = String(active.id);
        if (overId === activeId) return null;
        if (isDashboardGapId(overId) && Number.isNaN(parseInt(overId.slice(GAP_ID_PREFIX.length), DECIMAL_RADIX))) return null;
        const target: DropTarget = { id: overId, side: isDashboardGapId(overId) ? "before" : dashboardDropSide(over.rect, dragPointRef.current, isMobile) };
        const order = dragStartOrderRef.current ?? widgetOrder;
        return sameRows(applyDrop(order, activeId, target), order) ? null : target;
    };
    const trackDropTarget = ({ active, over }: { active: Active; over: Over | null }) => {
        const next = resolveDropTarget(active, over);
        setDropTarget((current) => current?.id === next?.id && current?.side === next?.side ? current : next);
    };
    const endDrag = () => {
        setActiveDragId(null);
        setActiveDragWidth(null);
        setDropTarget(null);
        dragStartOrderRef.current = null;
    };
    const handleDragEnd = (event: DragEndEvent) => {
        const sourceOrder = dragStartOrderRef.current ?? widgetOrder;
        const target = resolveDropTarget(event.active, event.over);
        const activeId = String(event.active.id);
        endDrag();
        if (target) setWidgetOrder(() => applyDrop(sourceOrder, activeId, target));
    };
    return (<div inert={disabled} aria-busy={disabled} style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>{configMode ? (<div>
            <DndContext sensors={sensors} collisionDetection={collisionDetection} onDragStart={({ active }) => {
            setActiveDragId(String(active.id));
            setActiveDragWidth(active.rect.current.initial?.width ?? null);
            setDropTarget(null);
            dragStartOrderRef.current = [...widgetOrder];
        }} onDragMove={trackDropTarget} onDragOver={trackDropTarget} onDragEnd={handleDragEnd} onDragCancel={endDrag}>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <RowGapDroppable gapId={`gap-${gapTargetIndexes[0]}`} marked={dropTarget?.id === `gap-${gapTargetIndexes[0]}`}/>
                {configModeRowsFiltered.map((row, rowIdx) => (<React.Fragment key={row.sourceRowIndex}>
                    <SortableContext items={row.keys} strategy={NO_SHIFT_STRATEGY}>
                      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: spacing.lg, alignItems: "stretch", marginBottom: spacing.lg }}>
                        {row.keys.map((key, columnIdx) => {
                    const k = key as string;
                    const { cardTitle, cardExtra } = withCardIcon(getWidgetMeta(k));
                    const mark = dropTarget?.id === k
                        ? <CardDropMark side={dropTarget.side} stacked={isMobile} outerEdge={dropTarget.side === "before" ? columnIdx === 0 : columnIdx === row.keys.length - 1}/>
                        : null;
                    return (<SortableDashboardCard key={key} id={key} checked={isWidgetVisible(k)} onToggle={(v) => setWidgetVisible(k, v)} cardTitle={cardTitle} cardExtra={cardExtra} periodSelector={renderPeriodSelector(k)} dropMark={mark} controls={controls}>
                              {renderWidgetBody(k)}
                            </SortableDashboardCard>);
                })}
                      </div>
                    </SortableContext>
                    <RowGapDroppable gapId={`gap-${gapTargetIndexes[rowIdx + 1]}`} marked={dropTarget?.id === `gap-${gapTargetIndexes[rowIdx + 1]}`}/>
                  </React.Fragment>))}
              </div>
              <DragOverlay dropAnimation={null}>
                {activeDragId && allWidgetKeys.includes(activeDragId as string) ? (() => {
            const dk = activeDragId as string;
            const { cardTitle, cardExtra } = withCardIcon(getWidgetMeta(dk));
            // Kompakt und deckend: Eine Vorschau in voller Kartenbreite verdeckt halbe Zeilen und damit
            // genau die Einfügemarke, nach der man zielt. Der Griff sitzt links, bleibt also unter dem Zeiger.
            return (<div style={{ width: Math.min(activeDragWidth ?? DRAG_PREVIEW_MAX_WIDTH, DRAG_PREVIEW_MAX_WIDTH), minWidth: 200, transform: "rotate(-1.5deg)", transformOrigin: "left center" }}>
                      <Card style={{ borderStyle: "dashed", cursor: "grabbing", background: "var(--ant-color-bg-elevated)", boxShadow: "var(--ant-box-shadow)", animation: "none", display: "flex", flexDirection: "column" }} styles={{ body: { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" } }}>
                        <div style={{ display: "flex", alignItems: "stretch", gap: spacing.md, minHeight: 60, flex: 1 }}>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: designSpacing.controlCompact, flexShrink: 0 }}>
                            <DashboardCardCheckmark checked={isWidgetVisible(dk)} onChange={() => { }} label={controls.show}/>
                            <div style={{ cursor: "grabbing", display: "flex", flex: 1, alignItems: "center", justifyContent: "center", padding: spacing.xs }}>
                              <HolderOutlined style={{ color: "var(--ant-color-text-tertiary)", fontSize: designTypography.fontSize.md }}/>
                            </div>
                          </div>
                          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
                            {(cardTitle != null || cardExtra != null) && (<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: spacing.sm, flexWrap: "wrap", marginBottom: spacing.sm }}>
                                <span className="mq-dashboard-card-title" style={{ minWidth: 0, overflowWrap: "anywhere" }}>{cardTitle}</span>
                                {cardExtra}
                              </div>)}
                            <div style={{ flex: 1 }}>{renderWidgetBody(dk)}</div>
                          </div>
                        </div>
                      </Card>
                    </div>);
        })() : null}
              </DragOverlay>
            </DndContext>
          </div>) : (<div style={isMobile ? { display: "flex", flexDirection: "column", gap: spacing.lg } : DASHBOARD_ROWS_FILL_STYLE}>
            {displayRows.map((rowKeys, rowIdx) => {
            // Reihen mit Listen teilen sich die Resthöhe; reine Kennzahlreihen bleiben so hoch, wie sie sind.
            const hasList = rowKeys.some((key) => LIST_WIDGET_KEYS.has(key as string));
            const rowStyle: React.CSSProperties = isMobile
                ? { display: "flex", flexDirection: "column", gap: spacing.lg, alignItems: "stretch" }
                : { display: "flex", flexDirection: "row", gap: spacing.lg, alignItems: "stretch", ...(hasList ? DASHBOARD_LIST_ROW_STYLE : DASHBOARD_STAT_ROW_STYLE) };
            return (<div key={rowIdx} style={rowStyle}>
                {rowKeys.map((key) => {
                    const k = key as string;
                    if (!isWidgetVisible(k))
                        return null;
                    const { cardTitle, cardExtra, loading } = withCardIcon(getWidgetMeta(k));
                    // Der Zeitraum steht nur an Karten mit Titel; Kennzahlkarten führen ihn im Titel des Inhalts.
                    const period = cardTitle != null ? periodLabel(k) : null;
                    const open = loading ? undefined : widgetTarget(k);
                    return (<div key={key} style={{ flex: 1, minWidth: 0, display: "flex" }}>
                      <Card loading={loading} hoverable={Boolean(open)} role={open ? "link" : undefined} tabIndex={open ? 0 : undefined} onClick={open ? (e) => { if (!fromInnerControl(e)) open(); } : undefined} onKeyDown={open ? (e) => { if ((e.key === KEY_ENTER || e.key === KEY_SPACE) && !fromInnerControl(e)) {
                        e.preventDefault();
                        open();
                    } } : undefined} style={{ display: "flex", flexDirection: "column", width: "100%", cursor: open ? "pointer" : undefined }} styles={{ body: { flex: 1, display: "flex", flexDirection: "column", minHeight: 0 } }}>
                        {(cardTitle != null || cardExtra != null) && (<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: spacing.sm, flexWrap: "wrap", marginBottom: spacing.sm, flexShrink: 0 }}>
                            <span className="mq-dashboard-card-title" style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                              {cardTitle}
                              {period ? <Typography.Text type="secondary"> · {period}</Typography.Text> : null}
                            </span>
                            {/* Der Kopf-Link navigiert selbst; die klickbare Karte darf nicht ein zweites Mal navigieren. */}
                            {cardExtra != null ? <span onClick={(e) => e.stopPropagation()}>{cardExtra}</span> : null}
                          </div>)}
                        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                          {renderWidgetBody(k)}
                        </div>
                      </Card>
                    </div>);
                })}
              </div>);
        })}
          </div>)}</div>);
}
